import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Key,
  Plus,
  Copy,
  Check,
  Gift,
  Crown,
  Calendar,
  List,
  Loader2,
  Trash2,
  Search,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { useTranslation } from "react-i18next";

export default function AdminCodeGeneratorModule() {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin"]);

  const [loading, setLoading] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState([]);
  const [allCodes, setAllCodes] = useState([]);
  const [showCodesModal, setShowCodesModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [availablePlans, setAvailablePlans] = useState([]);

  // Form state
  const [plan, setPlan] = useState("pro");
  const [duration, setDuration] = useState("30");
  const [quantity, setQuantity] = useState("1");
  const [isGift, setIsGift] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    fetchAllCodes();
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/public/plans`);
      if (response.ok) {
        const plans = await response.json();
        // Filter only active plans and exclude free plan
        const activePlans = plans.filter(p => p.is_active !== false && p.id !== 'free');
        setAvailablePlans(activePlans);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchAllCodes = async () => {
    try {
      const response = await fetch(`${API}/activation-codes/list`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // API returns {total, codes} object
        setAllCodes(data.codes || []);
      }
    } catch (error) {
      console.error("Error fetching codes:", error);
      setAllCodes([]);
    }
  };

  const handleGenerateCodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/activation-codes/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan,
          duration_days: parseInt(duration),
          quantity: parseInt(quantity),
          is_gift: isGift,
          notes: notes || null,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(`${data.codes.length} kod başarıyla oluşturuldu!`);
        setGeneratedCodes(data.codes);
        setShowCodesModal(true);
        fetchAllCodes();
        // Reset form
        setQuantity("1");
        setNotes("");
        setIsGift(false);
      } else {
        toast.error(data.detail || t("admin:codeCreateFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (code, index) => {
    const codeText = String(code);
    
    // Method 1: Modern Clipboard API
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(codeText);
        setCopiedIndex(index);
        toast.success(t('common:codeCopied'));
        setTimeout(() => setCopiedIndex(null), 2000);
        return;
      } catch (err) {
        console.log("Clipboard API failed, trying fallback");
      }
    }
    
    // Method 2: execCommand with input element (works better in modals)
    const input = document.createElement("input");
    input.value = codeText;
    input.setAttribute("readonly", "");
    input.style.position = "absolute";
    input.style.left = "-9999px";
    document.body.appendChild(input);
    
    // Select for iOS
    input.setSelectionRange(0, 99999);
    input.select();
    
    try {
      const success = document.execCommand("copy");
      if (success) {
        setCopiedIndex(index);
        toast.success(t('common:codeCopied'));
        setTimeout(() => setCopiedIndex(null), 2000);
      } else {
        // Method 3: Show code in prompt for manual copy
        window.prompt(t("admin:codeCopyPrompt"), codeText);
      }
    } catch (err) {
      window.prompt(t("admin:codeCopyPrompt"), codeText);
    } finally {
      document.body.removeChild(input);
    }
  };

  const copyAllCodes = async () => {
    const codesText = generatedCodes
      .map((c) => {
        const foundPlan = availablePlans.find(p => p.id === c.plan);
        const planName = foundPlan?.name || c.plan;
        return `${c.code_display || c.code} (${planName} - ${c.duration_days} gün)`;
      })
      .join("\n");
    try {
      await navigator.clipboard.writeText(codesText);
      toast.success(t('common:allCodesCopied'));
    } catch (error) {
      const textArea = document.createElement("textarea");
      textArea.value = codesText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(t('common:allCodesCopied'));
    }
  };

  const getPlanBadge = (planType) => {
    // Find plan name from available plans
    const foundPlan = availablePlans.find(p => p.id === planType);
    const planName = foundPlan?.name || planType;
    
    switch (planType) {
      case "pro":
        return <Badge className="bg-blue-100 text-blue-700">{planName}</Badge>;
      case "free":
        return <Badge className="bg-gray-100 text-gray-700">{t('common:free')}</Badge>;
      default:
        return <Badge className="bg-green-100 text-green-700">{planName}</Badge>;
    }
  };

  const getDurationLabel = (days) => {
    switch (days) {
      case 7: return "1 Hafta";
      case 30: return "1 Ay";
      case 90: return "3 Ay";
      case 180: return "6 Ay";
      case 365: return t("common:oneYear");
      default: return `${days} gün`;
    }
  };

  const filteredCodes = allCodes.filter(
    (code) =>
      code.code_display?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      code.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Generate Code Form */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Key className="w-5 h-5 text-navy" />
            Aktivasyon Kodu Oluştur
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Plan Selection */}
            <div>
              <Label>{t("admin:planType")}</Label>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availablePlans.length > 0 ? (
                    availablePlans.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        <div className="flex items-center gap-2">
                          <Crown className={`w-4 h-4 ${p.id === 'pro' ? 'text-blue-600' : 'text-gray-500'}`} />
                          {p.name}
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="pro">Pro Plan</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Duration Selection */}
            <div>
              <Label>{t("common:duration")}</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">1 Hafta (Deneme)</SelectItem>
                  <SelectItem value="30">1 Ay</SelectItem>
                  <SelectItem value="90">3 Ay</SelectItem>
                  <SelectItem value="180">6 Ay</SelectItem>
                  <SelectItem value="365">1 Yıl</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Quantity */}
            <div>
              <Label>{t("common:quantity")}</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="rounded-xl mt-1"
              />
            </div>

            {/* Gift Toggle */}
            <div className="flex items-center justify-between p-3 bg-pink-50 rounded-xl">
              <div className="flex items-center gap-2">
                <Gift className="w-4 h-4 text-pink-600" />
                <Label className="text-pink-700">Hediye Kodu</Label>
              </div>
              <Switch checked={isGift} onCheckedChange={setIsGift} />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>{t("common:noteOptional")}</Label>
            <Textarea
              placeholder={t("admin:ph_koduKiminIcinOlusturdugunuzuYazabilirsiniz")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="rounded-xl mt-1"
              rows={2}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleGenerateCodes}
              disabled={loading}
              className="flex-1 bg-navy hover:bg-navy/90 rounded-xl h-11"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Kod Oluştur
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowListModal(true)}
              className="rounded-xl h-11"
            >
              <List className="w-4 h-4 mr-2" />
              Tüm Kodlar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-0 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Crown className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800">{t("admin:kodKullanimi")}</p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                <li>Oluşturulan kodları müşterilere e-posta veya mesaj ile gönderin</li>
                <li>Müşteri kodu Dashboard → Plan Yükselt bölümünden aktifleştirir</li>
                <li>Hediye kodları kampanya ve promosyonlarda kullanılabilir</li>
                <li>Kodlar 1 yıl boyunca geçerlidir</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Generated Codes Modal */}
      <Dialog open={showCodesModal} onOpenChange={setShowCodesModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              Kodlar Oluşturuldu
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {generatedCodes.map((code, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Input
                      id={`code-input-${index}`}
                      readOnly
                      value={code.code_display || code.code}
                      className="font-mono font-bold text-navy text-lg bg-white"
                      onClick={(e) => {
                        e.target.select();
                        copyToClipboard(code.code_display || code.code, index);
                      }}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const input = document.getElementById(`code-input-${index}`);
                        if (input) {
                          input.select();
                          input.setSelectionRange(0, 99999);
                        }
                        copyToClipboard(code.code_display || code.code, index);
                      }}
                      className="rounded-lg shrink-0"
                    >
                      {copiedIndex === index ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {getPlanBadge(code.plan)}
                    <span className="text-xs text-grey">
                      {getDurationLabel(code.duration_days)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {generatedCodes.length > 1 && (
              <Button
                variant="outline"
                onClick={copyAllCodes}
                className="w-full rounded-xl"
              >
                <Copy className="w-4 h-4 mr-2" />
                Tümünü Kopyala
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* All Codes List Modal */}
      <Dialog open={showListModal} onOpenChange={setShowListModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="w-5 h-5" />
              Tüm Aktivasyon Kodları
            </DialogTitle>
          </DialogHeader>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
            <Input
              placeholder="Kod veya not ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>

          <div className="space-y-2 mt-4 max-h-96 overflow-y-auto">
            {filteredCodes.length > 0 ? (
              filteredCodes.map((code) => (
                <div
                  key={code.code_id}
                  className={`p-3 rounded-xl border ${
                    code.is_used ? "bg-gray-50 border-gray-200" : "bg-white border-gray-100"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <code className={`font-mono font-bold ${
                        code.is_used ? "text-gray-400" : "text-navy"
                      }`}>
                        {code.code_display}
                      </code>
                      {getPlanBadge(code.plan)}
                      {code.is_gift && (
                        <Badge className="bg-pink-100 text-pink-600">
                          <Gift className="w-3 h-3 mr-1" />
                          Hediye
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {code.is_used ? (
                        <Badge className="bg-gray-100 text-gray-600">Kullanıldı</Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(code.code_display, code.code_id)}
                          className="rounded-lg"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-grey">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {getDurationLabel(code.duration_days)}
                    </span>
                    <span>
                      Oluşturulma: {format(new Date(code.created_at), "dd/MM/yyyy", { locale: getDateLocale() })}
                    </span>
                    {code.notes && (
                      <span className="text-blue-600">Not: {code.notes}</span>
                    )}
                  </div>
                  {code.is_used && code.used_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      Kullanım: {format(new Date(code.used_at), "dd/MM/yyyy, HH:mm", { locale: getDateLocale() })}
                    </p>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-grey">
                <Key className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>{t("admin:henuzKodOlusturulmamis")}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
